import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Select from 'react-select';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FiXCircle, FiPlus, FiMove } from 'react-icons/fi';

import SpinnerThreeDots from '../../../components/LoadingSpinner/SpinnerThreeDots';
import Button from '../../../components/Button';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';
import EnumSurveySummaryType from '../../../utils/enums/EnumSurveySummaryType';

import {
  SummaryContainer,
  SummarySaveButtonWrapper,
  SummaryRowsContainer,
  SummaryRow,
  SummaryQuestionField,
  SummaryTypeField,
  SummaryCustomTextInput,
  SummaryRemoveButton,
  SummaryAddButton,
  SummaryDragHandle,
  SummaryGroupDivider,
} from './styles';

type SummaryType = EnumSurveySummaryType;

interface SurveyQuestion {
  questionIdComplete: string | number;
  questionTitle: string;
  type: string;
  text?: string;
}

interface SurveySummaryRecord {
  questionId: string;
  questionIdComplete?: string | number;
  customQuestionText: string;
  type: SummaryType;
  mainCardTitle: string;
  question?: SurveyQuestion;
  questionTitle?: string;
  questionType?: string;
  text?: string;
}

interface SurveySummaryResponse {
  items?: SurveySummaryRecord[];
  questions?: SurveyQuestion[];
}

interface RowState {
  localId: string;
  questionId: string | null;
  questionType: string | null;
  summaryType: SummaryType | null;
  customQuestionText: string;
  mainCardTitle: string;
}

interface SummaryTabProps {
  surveyId: string;
  loading: boolean;
  surveyIsLocked: boolean;
}

const SUMMARY_TYPES_SINGLE_VALUE: SummaryType[] = [
  EnumSurveySummaryType.MainCard,
  EnumSurveySummaryType.ListItem,
  EnumSurveySummaryType.OpenQuestion,
];
const SUMMARY_TYPES_MULTI_VALUE: SummaryType[] = [
  EnumSurveySummaryType.CheckBox,
];

const isCheckboxQuestionType = (questionType: string | null): boolean =>
  questionType === 'SelectMultiple';

const summaryTypesForQuestionType = (
  questionType: string | null,
): SummaryType[] => {
  if (isCheckboxQuestionType(questionType)) return SUMMARY_TYPES_MULTI_VALUE;
  return SUMMARY_TYPES_SINGLE_VALUE;
};

const createEmptyRow = (): RowState => ({
  localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  questionId: null,
  questionType: null,
  summaryType: null,
  customQuestionText: '',
  mainCardTitle: '',
});

const sortWithMainCardsFirst = (rows: RowState[]): RowState[] => {
  const mainCards = rows.filter(
    row => row.summaryType === EnumSurveySummaryType.MainCard,
  );
  const others = rows.filter(
    row => row.summaryType !== EnumSurveySummaryType.MainCard,
  );
  return [...mainCards, ...others];
};

type RowGroup = 'MAIN_CARD' | 'OTHER';

const getRowGroup = (row: RowState): RowGroup =>
  row.summaryType === EnumSurveySummaryType.MainCard ? 'MAIN_CARD' : 'OTHER';

const ROW_DRAG_TYPE: Record<RowGroup, string> = {
  MAIN_CARD: 'summary-row-main-card',
  OTHER: 'summary-row-other',
};

interface DragItem {
  index: number;
  localId: string;
}

interface DraggableSummaryRowProps {
  row: RowState;
  index: number;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
  disabled: boolean;
  children: React.ReactNode;
}

const DraggableSummaryRow: React.FC<DraggableSummaryRowProps> = ({
  row,
  index,
  moveRow,
  disabled,
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const dragType = ROW_DRAG_TYPE[getRowGroup(row)];

  const [, drop] = useDrop<DragItem, void, void>({
    accept: dragType,
    hover: (item, monitor) => {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset?.y ?? 0) - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveRow(dragIndex, hoverIndex);
      // eslint-disable-next-line no-param-reassign
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: dragType,
    item: (): DragItem => ({ index, localId: row.localId }),
    canDrag: !disabled,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  return (
    <SummaryRow ref={ref} $isDragging={isDragging}>
      <SummaryDragHandle $disabled={disabled} title="Drag to reorder">
        <FiMove size={18} />
      </SummaryDragHandle>
      {children}
    </SummaryRow>
  );
};

const SummaryTab: React.FC<SummaryTabProps> = ({
  surveyId,
  loading,
  surveyIsLocked,
}) => {
  const { addToast } = useToast();
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [rows, setRows] = useState<RowState[]>([createEmptyRow()]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!surveyId) return undefined;

    let cancelled = false;
    setInternalLoading(true);

    api
      .get(`/surveySummary/survey/${surveyId}?includeQuestions=true`)
      .then(summaryRes => {
        if (cancelled) return;

        const responseData = summaryRes.data as
          | SurveySummaryRecord[]
          | SurveySummaryResponse
          | undefined;
        const records: SurveySummaryRecord[] = Array.isArray(responseData)
          ? responseData
          : responseData?.items ?? [];
        const fetchedQuestions = (
          !Array.isArray(responseData) ? responseData?.questions ?? [] : []
        ).reduce<SurveyQuestion[]>((questionList, question) => {
          const questionIdComplete = String(question.questionIdComplete);
          if (
            !questionList.some(
              item => String(item.questionIdComplete) === questionIdComplete,
            )
          ) {
            questionList.push({
              ...question,
              questionIdComplete,
              questionTitle:
                question.questionTitle ?? question.text ?? questionIdComplete,
            });
          }
          return questionList;
        }, []);
        const summaryQuestions = records.reduce<SurveyQuestion[]>(
          (questionList, record) => {
            const questionIdComplete = String(
              record.questionIdComplete ??
                record.question?.questionIdComplete ??
                record.questionId,
            );
            const questionData = record.question ?? {
              questionIdComplete,
              questionTitle: record.questionTitle ?? record.text ?? '',
              type: record.questionType ?? '',
            };
            if (
              questionList.some(
                question =>
                  String(question.questionIdComplete) === questionIdComplete,
              )
            ) {
              return questionList;
            }

            questionList.push({
              ...questionData,
              questionIdComplete,
              questionTitle:
                questionData.questionTitle ??
                questionData.text ??
                questionIdComplete,
            });
            return questionList;
          },
          [],
        );
        setQuestions([...fetchedQuestions, ...summaryQuestions]);

        if (records.length > 0) {
          const hydrated = records.map(r => {
            const completeId = String(
              r.questionIdComplete ??
                r.question?.questionIdComplete ??
                r.questionId,
            );
            const questionType =
              r.question?.type ??
              [...fetchedQuestions, ...summaryQuestions].find(
                question => String(question.questionIdComplete) === completeId,
              )?.type ??
              null;
            return {
              localId: `${completeId}-${Math.random().toString(36).slice(2)}`,
              questionId: completeId,
              questionType,
              summaryType: isCheckboxQuestionType(questionType)
                ? EnumSurveySummaryType.CheckBox
                : r.type,
              customQuestionText: r.customQuestionText ?? '',
              mainCardTitle: r.mainCardTitle ?? '',
            };
          });
          setRows(sortWithMainCardsFirst(hydrated));
        } else {
          setRows([createEmptyRow()]);
        }
      })
      .catch(() => {
        if (cancelled) return;
        addToast({
          type: 'error',
          title: 'Load error',
          description: 'Failed to load summary data.',
        });
      })
      .finally(() => {
        if (!cancelled) setInternalLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [surveyId, addToast]);

  const selectedQuestionIds = useMemo(
    () => new Set(rows.map(r => r.questionId).filter((q): q is string => !!q)),
    [rows],
  );

  const questionOptionsForRow = useCallback(
    (currentQuestionId: string | null) =>
      questions
        .filter(
          q =>
            String(q.questionIdComplete) === currentQuestionId ||
            !selectedQuestionIds.has(String(q.questionIdComplete)),
        )
        .map(q => ({
          value: String(q.questionIdComplete),
          label: q.questionTitle ?? String(q.questionIdComplete),
          questionType: q.type,
        })),
    [questions, selectedQuestionIds],
  );

  const summaryTypeOptionsFor = useCallback((questionType: string | null) => {
    if (!questionType) return [];
    return summaryTypesForQuestionType(questionType).map(t => ({
      value: t,
      label: t,
    }));
  }, []);

  const buildQuestionSelectValue = useCallback(
    (questionId: string | null) => {
      if (!questionId) return null;
      const match = questions.find(
        q => String(q.questionIdComplete) === questionId,
      );
      const label = match?.questionTitle ?? questionId;
      return { value: questionId, label };
    },
    [questions],
  );

  const buildSummaryTypeSelectValue = useCallback(
    (summaryType: SummaryType | null) =>
      summaryType ? { value: summaryType, label: summaryType } : null,
    [],
  );

  const handleQuestionChange = useCallback((localId: string, option: any) => {
    setRows(prev =>
      prev.map(row => {
        if (row.localId !== localId) return row;
        const newQuestionId = option?.value ?? null;
        const newQuestionType = option?.questionType ?? null;
        const allowed = summaryTypesForQuestionType(newQuestionType);
        const summaryType = isCheckboxQuestionType(newQuestionType)
          ? EnumSurveySummaryType.CheckBox
          : row.summaryType && allowed.includes(row.summaryType)
          ? row.summaryType
          : null;
        return {
          ...row,
          questionId: newQuestionId,
          questionType: newQuestionType,
          summaryType,
        };
      }),
    );
  }, []);

  const handleSummaryTypeChange = useCallback(
    (localId: string, option: any) => {
      setRows(prev => {
        const updated = prev.map(row => {
          if (row.localId !== localId) return row;
          const summaryType = (option?.value as SummaryType) ?? null;
          return {
            ...row,
            summaryType,
            mainCardTitle:
              summaryType === EnumSurveySummaryType.MainCard
                ? row.mainCardTitle
                : '',
          };
        });
        return sortWithMainCardsFirst(updated);
      });
    },
    [],
  );

  const handleTextChange = useCallback((localId: string, value: string) => {
    setRows(prev =>
      prev.map(row =>
        row.localId === localId ? { ...row, customQuestionText: value } : row,
      ),
    );
  }, []);

  const handleMainCardTitleChange = useCallback(
    (localId: string, value: string) => {
      setRows(prev =>
        prev.map(row =>
          row.localId === localId ? { ...row, mainCardTitle: value } : row,
        ),
      );
    },
    [],
  );

  const handleAddRow = useCallback(() => {
    setRows(prev => [...prev, createEmptyRow()]);
  }, []);

  const handleRemoveRow = useCallback((localId: string) => {
    setRows(prev => {
      const next = prev.filter(row => row.localId !== localId);
      return next.length > 0 ? next : [createEmptyRow()];
    });
  }, []);

  const moveRow = useCallback((dragIndex: number, hoverIndex: number) => {
    setRows(prev => {
      const updated = [...prev];
      const [dragged] = updated.splice(dragIndex, 1);
      updated.splice(hoverIndex, 0, dragged);
      return sortWithMainCardsFirst(updated);
    });
  }, []);

  const handleSaveChanges = useCallback(async () => {
    const filledRows = rows.filter(
      r =>
        r.questionId !== null || r.summaryType !== null || r.customQuestionText,
    );

    const incomplete = filledRows.some(r => !r.questionId || !r.summaryType);
    if (incomplete) {
      addToast({
        type: 'error',
        title: 'Incomplete rows',
        description:
          'Please select a question and a summary type for every row.',
      });
      return;
    }

    const missingMainCardTitle = filledRows.some(
      r =>
        r.summaryType === EnumSurveySummaryType.MainCard &&
        !r.mainCardTitle.trim(),
    );
    if (missingMainCardTitle) {
      addToast({
        type: 'error',
        title: 'Missing main card title',
        description:
          'Please provide a main card title for every Main Card row.',
      });
      return;
    }

    const items = filledRows.map(r => ({
      questionId: `${r.questionId}`,
      customQuestionText: r.customQuestionText,
      type: r.summaryType as SummaryType,
      mainCardTitle:
        r.summaryType === EnumSurveySummaryType.MainCard ? r.mainCardTitle : '',
    }));

    try {
      setSaving(true);
      await api.put(`/surveySummary/survey/${surveyId}`, { items });

      addToast({
        type: 'success',
        title: 'Saved',
        description: 'Summary configuration saved.',
      });
    } catch (err) {
      console.log(err);
      addToast({
        type: 'error',
        title: 'Save error',
        description: 'Failed to save summary configuration.',
      });
    } finally {
      setSaving(false);
    }
  }, [rows, surveyId, addToast]);

  return (
    <SummaryContainer>
      {!surveyIsLocked && (
        <SummarySaveButtonWrapper>
          <Button
            type="button"
            onClick={handleSaveChanges}
            disabled={saving || internalLoading}
            $width="275px"
          >
            Save Changes
          </Button>
        </SummarySaveButtonWrapper>
      )}

      <SpinnerThreeDots loading={loading || internalLoading}>
        <SummaryRowsContainer>
          <DndProvider backend={HTML5Backend}>
            {rows.map((row, index) => {
              const showDividerBefore =
                index > 0 &&
                getRowGroup(rows[index - 1]) === 'MAIN_CARD' &&
                getRowGroup(row) !== 'MAIN_CARD';

              return (
                <React.Fragment key={row.localId}>
                  {showDividerBefore && <SummaryGroupDivider />}
                  <DraggableSummaryRow
                    row={row}
                    index={index}
                    moveRow={moveRow}
                    disabled={surveyIsLocked}
                  >
                    <SummaryQuestionField>
                      <Select
                        placeholder="Select question"
                        isDisabled={surveyIsLocked}
                        options={questionOptionsForRow(row.questionId)}
                        value={buildQuestionSelectValue(row.questionId)}
                        onChange={option =>
                          handleQuestionChange(row.localId, option)
                        }
                      />
                    </SummaryQuestionField>

                    <SummaryTypeField>
                      <Select
                        placeholder="Type"
                        isDisabled={
                          surveyIsLocked ||
                          !row.questionType ||
                          isCheckboxQuestionType(row.questionType)
                        }
                        options={summaryTypeOptionsFor(row.questionType)}
                        value={buildSummaryTypeSelectValue(row.summaryType)}
                        onChange={option =>
                          handleSummaryTypeChange(row.localId, option)
                        }
                      />
                    </SummaryTypeField>

                    <SummaryCustomTextInput
                      type="text"
                      placeholder="Custom question text (optional)"
                      value={row.customQuestionText}
                      disabled={surveyIsLocked}
                      onChange={e =>
                        handleTextChange(row.localId, e.target.value)
                      }
                    />

                    <SummaryCustomTextInput
                      type="text"
                      placeholder="Main card title"
                      value={row.mainCardTitle}
                      disabled={
                        surveyIsLocked ||
                        row.summaryType !== EnumSurveySummaryType.MainCard
                      }
                      onChange={e =>
                        handleMainCardTitleChange(row.localId, e.target.value)
                      }
                    />

                    <SummaryRemoveButton
                      type="button"
                      onClick={() => handleRemoveRow(row.localId)}
                      disabled={surveyIsLocked}
                      title="Remove row"
                    >
                      <FiXCircle size={22} />
                    </SummaryRemoveButton>
                  </DraggableSummaryRow>
                </React.Fragment>
              );
            })}
          </DndProvider>

          {!surveyIsLocked && (
            <SummaryAddButton
              type="button"
              onClick={handleAddRow}
              title="Add row"
            >
              <FiPlus size={18} />
              Add question
            </SummaryAddButton>
          )}
        </SummaryRowsContainer>
      </SpinnerThreeDots>
    </SummaryContainer>
  );
};

export default SummaryTab;
